<template>
    <div>
        <div class="mb-4 flex items-baseline gap-2">
            <h2 class="text-lg font-extrabold text-foxy-text">Коментарі</h2>
            <span class="text-sm font-semibold text-foxy-text-secondary"
                >· <span class="replies-count">{{ totalRepliesCount }}</span></span
            >
        </div>

        <div v-if="isAuthenticated" class="reply-form active mb-7 flex items-start gap-3">
            <user-avatar
                :src="currentUser && currentUser.avatar_display ? currentUser.avatar_display : ''"
                :name="currentUser && currentUser.name ? currentUser.name : 'Користувач'"
                size="sm"
                class="mt-1 shrink-0 bg-foxy-elevated font-semibold text-foxy-text"
            />
            <div class="reply-input-wrapper relative min-w-0 flex-1">
                <div class="relative">
                    <textarea
                        v-model="mainReplyContent"
                        ref="mainReplyTextarea"
                        rows="2"
                        :maxlength="maxLength"
                        class="foxy-textarea reply-textarea w-full resize-none !rounded-xl !px-4 !py-3 transition-all duration-200"
                        :class="{ 'border-red-300 focus:ring-red-500 dark:border-red-700': mainReplyContent.length >= maxLength }"
                        placeholder="Напишіть коментар…"
                        @focus="expandTextarea"
                        @blur="collapseTextarea"
                        @input="handleMainReplyInput"
                        @keydown="handleMainReplyKeydown"
                        required
                    ></textarea>

                    <div
                        v-if="showMentionDropdown && mentionUsers.length > 0"
                        class="absolute z-[9999] mt-1 max-h-60 overflow-y-auto rounded-xl border border-foxy-border bg-foxy-surface shadow-xl"
                        :style="{ top: '100%', left: '0px', minWidth: '250px', maxWidth: '300px' }"
                    >
                        <div
                            v-for="(user, index) in mentionUsers"
                            :key="user.id"
                            :class="[
                                'flex cursor-pointer items-center space-x-3 px-4 py-3 transition-colors',
                                selectedMentionIndex === index ? 'bg-foxy-action/10 text-foxy-action' : 'hover:bg-foxy-elevated',
                            ]"
                            @click="insertMainMention(user.username)"
                            @mouseenter="selectedMentionIndex = index"
                        >
                            <user-avatar :src="user.avatar_display || ''" :name="user.name" size="sm" />
                            <div class="min-w-0 flex-1">
                                <div class="truncate text-sm font-medium text-foxy-text">{{ user.name }}</div>
                                <div class="truncate text-xs text-foxy-text-secondary">@{{ user.username }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-2 flex items-center justify-between gap-3">
                    <div class="text-xs text-foxy-text-secondary" :class="{ 'text-red-500 dark:text-red-400': mainReplyContent.length >= maxLength }">
                        {{ mainReplyContent.length }}/{{ maxLength }}
                    </div>
                    <button
                        type="button"
                        :disabled="isMainReplySubmitting || !mainReplyContent.trim() || mainReplyContent.length > maxLength"
                        class="inline-flex min-h-10 items-center rounded-xl bg-foxy-action px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-foxy-action/90 disabled:cursor-not-allowed disabled:opacity-50"
                        @click="submitMainReply"
                    >
                        <i v-if="isMainReplySubmitting" class="fas fa-spinner fa-spin mr-2" aria-hidden="true"></i>
                        {{ isMainReplySubmitting ? 'Надсилаємо…' : 'Надіслати' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-else class="mb-5 flex max-w-4xl flex-col gap-3 rounded-xl border border-foxy-border/80 bg-foxy-surface/65 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p class="text-sm text-foxy-text-secondary">Увійдіть або зареєструйтеся, щоб читати й писати коментарі.</p>
            <div class="flex shrink-0 gap-2">
                <a href="/login" class="acc-btn-primary">Увійти</a>
                <a href="/register" class="acc-btn">Реєстрація</a>
            </div>
        </div>

        <div v-if="isAuthenticated && localReplies.length > 0" id="comments-container" class="comments-tree divide-y divide-foxy-border/70">
                <review-reply
                    v-for="reply in localReplies"
                    :key="reply.id"
                    :reply="reply"
                    :level="0"
                    :book-slug="bookSlug"
                    :review-id="reviewId"
                    :current-user-id="currentUserId"
                    :is-moderator="isModerator"
                    :target-comment-id="targetCommentId"
                    @reply-added="handleReplyAdded"
                    @reply-updated="handleReplyUpdated"
                    @reply-deleted="handleReplyDeleted"
                    @like-toggled="handleLikeToggled"
                    @show-notification="showNotification"
                />
        </div>
        <div v-else-if="localReplies.length === 0" class="py-2 text-sm text-foxy-text-secondary">
            <p class="font-bold text-foxy-text">Коментарів ще немає</p>
            <p class="mt-1">Будьте першим, хто поділиться думкою.</p>
        </div>
    </div>
</template>

<script>
import ReviewReply from './ReviewReply.vue';
import UserAvatar from './UserAvatar.vue';
import axios from 'axios';

function reviewCommentIdFromHash() {
    const match = window.location.hash.match(/^#comment-(\d+)$/);

    return match ? Number(match[1]) : null;
}

export default {
    name: 'ReviewsRepliesList',
    components: {
        ReviewReply,
        UserAvatar,
    },
    props: {
        replies: {
            type: [Array, Object],
            default: () => []
        },
        bookSlug: {
            type: String,
            required: true
        },
        reviewId: {
            type: Number,
            required: true
        },
        currentUserId: {
            type: Number,
            default: null
        },
        currentUser: {
            type: Object,
            default: null
        },
        isModerator: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localReplies: Array.isArray(this.replies) ? [...this.replies] : Object.values(this.replies || {}),
            totalRepliesCount: 0,
            mainReplyContent: '',
            isMainReplySubmitting: false,
            maxLength: 400,
            // Mention autocomplete
            mentionUsers: [],
            showMentionDropdown: false,
            mentionPosition: { top: 0, left: 0 },
            mentionQuery: '',
            selectedMentionIndex: -1,
            mentionStartPos: -1,
            mentionSearchTimeout: null,
            targetCommentId: reviewCommentIdFromHash(),
            targetCommentObserver: null,
            targetCommentTimeout: null,
            targetHighlightTimeout: null,
        };
    },
    computed: {
        isAuthenticated() {
            return this.currentUserId !== null && this.currentUserId !== undefined && this.currentUserId !== '';
        }
    },
    methods: {
        containsReply(replies, targetId) {
            return replies.some(reply => Number(reply.id) === targetId
                || this.containsReply(reply.replies || [], targetId));
        },
        initializeCommentTarget() {
            if (!this.targetCommentId) {
                return;
            }

            if (!this.containsReply(this.localReplies, this.targetCommentId)) {
                this.$nextTick(() => this.showNotification('Коментар недоступний або був видалений.', 'info'));
                return;
            }

            this.$nextTick(() => {
                if (this.revealTargetComment()) {
                    return;
                }

                this.targetCommentObserver = new MutationObserver(() => this.revealTargetComment());
                this.targetCommentObserver.observe(this.$el, { childList: true, subtree: true });
                this.targetCommentTimeout = window.setTimeout(() => {
                    this.disconnectTargetObserver();
                    this.showNotification('Не вдалося показати потрібний коментар.', 'info');
                }, 2000);
            });
        },
        revealTargetComment() {
            const target = this.$el.querySelector(`#comment-${this.targetCommentId}`);
            if (!target) {
                return false;
            }

            this.disconnectTargetObserver();
            const addedTabIndex = !target.hasAttribute('tabindex');
            if (addedTabIndex) {
                target.setAttribute('tabindex', '-1');
            }
            target.setAttribute('aria-current', 'true');
            target.classList.add('notification-comment-target');

            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
            target.focus({ preventScroll: true });

            this.targetHighlightTimeout = window.setTimeout(() => {
                target.classList.remove('notification-comment-target');
                target.removeAttribute('aria-current');
                if (addedTabIndex) {
                    target.removeAttribute('tabindex');
                }
            }, 5000);

            return true;
        },
        disconnectTargetObserver() {
            if (this.targetCommentObserver) {
                this.targetCommentObserver.disconnect();
                this.targetCommentObserver = null;
            }
            if (this.targetCommentTimeout) {
                window.clearTimeout(this.targetCommentTimeout);
                this.targetCommentTimeout = null;
            }
        },
        expandTextarea() {
            if (this.$refs.mainReplyTextarea) {
                this.$refs.mainReplyTextarea.rows = 3;
            }
        },
        collapseTextarea() {
            if (this.$refs.mainReplyTextarea && !this.mainReplyContent.trim()) {
                this.$refs.mainReplyTextarea.rows = 1;
            }
        },
        validateLength() {
            if (this.mainReplyContent.length > this.maxLength) {
                this.mainReplyContent = this.mainReplyContent.substring(0, this.maxLength);
            }
        },
        
        handleMainReplyInput(event) {
            this.validateLength();
            
            const textarea = event.target;
            if (!textarea) return;
            
            // Используем setTimeout вместо $nextTick для гарантии обновления
            setTimeout(() => {
                const cursorPos = textarea.selectionStart;
                const currentValue = textarea.value || this.mainReplyContent;
                const textBeforeCursor = currentValue.substring(0, cursorPos);
                
                console.log('Input detected:', { cursorPos, currentValue, textBeforeCursor });
                
                const atIndex = textBeforeCursor.lastIndexOf('@');
                
                if (atIndex === -1) {
                    console.log('No @ found, hiding dropdown');
                    this.hideMentionDropdown();
                    return;
                }
                
                const textAfterAt = textBeforeCursor.substring(atIndex + 1);
                // Если после @ есть пробел или перенос строки, скрываем dropdown
                if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
                    console.log('Space or newline after @, hiding dropdown');
                    this.hideMentionDropdown();
                    return;
                }
                
                // Показываем dropdown сразу после @, без требования минимального количества символов
                const query = textAfterAt.trim();
                console.log('Searching users with query:', query);
                this.mentionQuery = query;
                this.mentionStartPos = atIndex;
                this.searchUsers(query);
                this.calculateMentionPosition(textarea);
            }, 10);
        },
        
        async searchUsers(query) {
            if (this.mentionSearchTimeout) {
                clearTimeout(this.mentionSearchTimeout);
            }
            
            console.log('searchUsers called with query:', query);
            
            // Уменьшаем задержку для более быстрой реакции
            this.mentionSearchTimeout = setTimeout(async () => {
                try {
                    const url = `/api/users/search${query ? '?q=' + encodeURIComponent(query) : ''}`;
                    console.log('Fetching from URL:', url);
                    const response = await fetch(url);
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const data = await response.json();
                    console.log('Users received:', data.users);
                    this.mentionUsers = data.users || [];
                    this.showMentionDropdown = this.mentionUsers.length > 0;
                    this.selectedMentionIndex = -1;
                    
                    console.log('Dropdown state:', { 
                        showMentionDropdown: this.showMentionDropdown, 
                        usersCount: this.mentionUsers.length 
                    });
                    
                    // Recalculate position after dropdown appears
                    if (this.showMentionDropdown) {
                        this.$nextTick(() => {
                            const textarea = this.$refs.mainReplyTextarea;
                            if (textarea) {
                                this.calculateMentionPosition(textarea);
                            }
                        });
                    }
                } catch (error) {
                    console.error('Error searching users:', error);
                    this.mentionUsers = [];
                    this.showMentionDropdown = false;
                }
            }, 200);
        },
        
        calculateMentionPosition(textarea) {
            // Позиционирование не нужно, используется top: 100% для размещения под textarea
            // Метод оставлен для совместимости, но не используется
        },
        
        insertMainMention(username) {
            const textarea = this.$refs.mainReplyTextarea;
            if (!textarea) return;
            
            const cursorPos = textarea.selectionStart;
            const currentValue = textarea.value || this.mainReplyContent;
            const textBeforeCursor = currentValue.substring(0, cursorPos);
            const textAfterCursor = currentValue.substring(cursorPos);
            
            const atIndex = textBeforeCursor.lastIndexOf('@');
            if (atIndex === -1) return;
            
            const beforeMention = currentValue.substring(0, atIndex);
            const afterMention = textAfterCursor;
            
            this.mainReplyContent = beforeMention + '@' + username + ' ' + afterMention;
            
            this.$nextTick(() => {
                const newCursorPos = atIndex + username.length + 2;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
                textarea.focus();
            });
            
            this.hideMentionDropdown();
        },
        
        handleMainReplyKeydown(event) {
            if (!this.showMentionDropdown || this.mentionUsers.length === 0) {
                return;
            }
            
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.selectedMentionIndex = Math.min(
                    this.selectedMentionIndex + 1,
                    this.mentionUsers.length - 1
                );
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.selectedMentionIndex = Math.max(this.selectedMentionIndex - 1, -1);
            } else if (event.key === 'Enter' || event.key === 'Tab') {
                if (this.selectedMentionIndex >= 0 && this.selectedMentionIndex < this.mentionUsers.length) {
                    event.preventDefault();
                    const selectedUser = this.mentionUsers[this.selectedMentionIndex];
                    this.insertMainMention(selectedUser.username);
                }
            } else if (event.key === 'Escape') {
                this.hideMentionDropdown();
            }
        },
        
        hideMentionDropdown() {
            this.showMentionDropdown = false;
            this.mentionUsers = [];
            this.selectedMentionIndex = -1;
            this.mentionQuery = '';
            this.mentionStartPos = -1;
            if (this.mentionSearchTimeout) {
                clearTimeout(this.mentionSearchTimeout);
                this.mentionSearchTimeout = null;
            }
        },
        async submitMainReply() {
            if (!this.mainReplyContent.trim()) {
                this.showNotification('Будь ласка, введіть текст коментаря.', 'error');
                return;
            }
            if (this.mainReplyContent.length > this.maxLength) {
                this.showNotification(`Коментар не може перевищувати ${this.maxLength} символів.`, 'error');
                return;
            }
            this.hideMentionDropdown();
            this.isMainReplySubmitting = true;
            try {
                const response = await axios.post(`/books/${this.bookSlug}/reviews/${this.reviewId}/replies`, {
                    content: this.mainReplyContent
                });
                if (response.data.success) {
                    this.mainReplyContent = '';
                    if (this.$refs.mainReplyTextarea) {
                        this.$refs.mainReplyTextarea.rows = 1;
                    }
                    this.handleReplyAdded(response.data.reply);
                    this.showNotification('Коментар додано.', 'success');
                } else {
                    this.showNotification(response.data.message || 'Не вдалося надіслати коментар.', 'error');
                }
            } catch (error) {
                console.error('Error submitting main reply:', error);
                this.showNotification(error.response?.data?.message || 'Не вдалося надіслати коментар. Спробуйте ще раз.', 'error');
            } finally {
                this.isMainReplySubmitting = false;
            }
        },
        handleReplyAdded(newReply) {
            // This is for nested replies being added
            // If the new reply is a direct child of the main review (level 0), add it to localReplies
            if (newReply.parent_id === this.reviewId) {
                this.localReplies.unshift(newReply);
                this.totalRepliesCount++;
            } else {
                // Find the parent and add the nested reply
                this.addNestedReply(this.localReplies, newReply);
                this.totalRepliesCount++;
            }
            this.updateRepliesCount();
        },
        addNestedReply(replies, newReply) {
            for (let i = 0; i < replies.length; i++) {
                if (replies[i].id === newReply.parent_id) {
                    if (!replies[i].replies) {
                        this.$set(replies[i], 'replies', []);
                    }
                    replies[i].replies.unshift(newReply);
                    this.$set(replies[i], 'replies_count', (replies[i].replies_count || 0) + 1);
                    return;
                }
                if (replies[i].replies && replies[i].replies.length > 0) {
                    this.addNestedReply(replies[i].replies, newReply);
                }
            }
        },
        handleReplyUpdated(updatedReply) {
            // Find and update the reply in localReplies or its nested replies
            this.updateReplyInList(this.localReplies, updatedReply);
        },
        updateReplyInList(replies, updatedReply) {
            for (let i = 0; i < replies.length; i++) {
                if (replies[i].id === updatedReply.id) {
                    this.$set(replies, i, updatedReply);
                    return;
                }
                if (replies[i].replies && replies[i].replies.length > 0) {
                    this.updateReplyInList(replies[i].replies, updatedReply);
                }
            }
        },
        handleReplyDeleted(deletedReplyId) {
            this.removeReplyFromList(this.localReplies, deletedReplyId);
            this.totalRepliesCount--;
            this.updateRepliesCount();
        },
        removeReplyFromList(replies, deletedReplyId) {
            for (let i = 0; i < replies.length; i++) {
                if (replies[i].id === deletedReplyId) {
                    replies.splice(i, 1);
                    return;
                }
                if (replies[i].replies && replies[i].replies.length > 0) {
                    const initialLength = replies[i].replies.length;
                    this.removeReplyFromList(replies[i].replies, deletedReplyId);
                    if (replies[i].replies.length < initialLength) {
                        this.$set(replies[i], 'replies_count', Math.max(0, (replies[i].replies_count || 0) - 1));
                    }
                }
            }
        },
        handleLikeToggled(payload) {
            // Update like state in the reply
            this.updateLikeInList(this.localReplies, payload);
        },
        updateLikeInList(replies, payload) {
            for (let i = 0; i < replies.length; i++) {
                if (replies[i].id === payload.replyId) {
                    this.$set(replies[i], 'is_liked_by_current_user', payload.isLiked);
                    this.$set(replies[i], 'likes_count', payload.likesCount);
                    return;
                }
                if (replies[i].replies && replies[i].replies.length > 0) {
                    this.updateLikeInList(replies[i].replies, payload);
                }
            }
        },
        updateRepliesCount() {
            // Update the total count in the UI
            const countElements = document.querySelectorAll('.replies-count');
            countElements.forEach(el => {
                el.textContent = this.totalRepliesCount;
            });
        },
        showNotification(message, type = 'info') {
            // Создаем красивое уведомление вместо alert
            const notification = document.createElement('div');
            notification.className = `notification fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 transform translate-x-full`;
            
            const colors = {
                success: 'bg-foxy-success text-white',
                error: 'bg-foxy-error text-white',
                warning: 'bg-foxy-warning text-white',
                info: 'bg-foxy-action text-white'
            };
            
            notification.className += ` ${colors[type] || colors.info}`;
            notification.textContent = message;
            
            document.body.appendChild(notification);
            
            // Анимация появления
            setTimeout(() => {
                notification.style.transform = 'translateX(0)';
            }, 100);
            
            // Автоматическое скрытие через 3 секунды
            setTimeout(() => {
                notification.style.transform = 'translateX(100%)';
                setTimeout(() => {
                    notification.remove();
                }, 300);
            }, 3000);
        },
        calculateTotalReplies() {
            let count = 0;
            const countReplies = (replies) => {
                replies.forEach(reply => {
                    count++;
                    if (reply.replies && reply.replies.length > 0) {
                        countReplies(reply.replies);
                    }
                });
            };
            countReplies(this.localReplies);
            this.totalRepliesCount = count;
        }
    },
    created() {
        // Calculate total replies count including nested ones
        this.calculateTotalReplies();
    },
    mounted() {
        this.initializeCommentTarget();
    },
    beforeDestroy() {
        this.disconnectTargetObserver();
        if (this.targetHighlightTimeout) {
            window.clearTimeout(this.targetHighlightTimeout);
        }
    }
};
</script>

<style scoped>
.reply-form {
    margin-bottom: 2rem;
}

.reply-form-content {
    padding: 0;
}

.reply-input-wrapper {
    display: flex;
    flex-direction: column;
}

.reply-textarea {
    min-height: 2.5rem;
    line-height: 1.5;
}

.reply-textarea:focus {
    min-height: 5rem;
}

.reply-buttons {
    display: flex;
    justify-content: flex-end;
}
</style>
